/* eslint-disable no-prototype-builtins */
import Reflux from "reflux";
import logger from "../../utils/LoggerFrontend";
import { ActionsAdAssistant } from "../../Actions/ActionsAdAssistant/ActionsAdAssistant";
import { processAutocroppedImage } from "../../utils/processAutocroppedImage";
import { getAdAssistantDimensions } from "./adAssistantDimensions";
import { getVideoDuration } from "../SelfServiceCampaignCreation/Previews/TemplatePost/TemplatePost__Preview/utils";
import { countShutterstockAssets, getShutterstockPreviewUrl } from "./utils";
import { hasParamsAlready } from "../SelfServiceCampaignCreation/Previews/media-assets-lab/layout/Layout";

// This should be only used to use the Actions.AddNotification, nothing else
import Actions from "../../Actions";

export default class AdAssistantStore extends Reflux.Store {
  constructor() {
    super();

    this.state = {
      uploadedChatbotAssets: [],
      uploadedImages: [],
      uploadedLogos: [],
      oldUploadedImages: [],
      jobTitle: "",
      jobTitles: [],
      loadingCloudinaryImages: false,
      text: {},
      // NEW STATES
      taskStep: 0,
      companyId: null,
      campaignData: {},
      loadingAssistantCampaign: false,
      loadingCombinations: false,
      allAdCombinations: [],
      usedAds: [],
      dismissedAds: [],
      currentAd: {},
      isEditModalOpen: false,
      isBooked: false,
      isWaitingForApproval: false,
      isActive: false,
      shutterstockImages: 0,
      shutterstockVideos: 0,
      totalShutterstockAssetsPrice: 0,
      isLocked: false,
      lockedBy: null,
      lockedUserId: null,
      // Version history
      adVersions: [], // List of ad versions
      currentAdVersion: null, // Current ad version
      isLoadingActiveAdVersion: false,
      isLoadingVersions: false, // Flag for version history
      isLoadingMoreVersions: false, // Flag for active version
      hasMoreVersions: true, // Flag for more versions
      versionsOffset: 0, // Offset for version history
      versionsLimit: 30 // Limit for version history
    };

    this.listenables = ActionsAdAssistant;
    this.onError = this.onError.bind(this);
  }

  onError(error) {
    logger.log(
      "error:AdAssistantStore:onError",
      "An error occurred in the Ad Assistant Store:",
      error
    );
  }

  onSetTaskStep(taskStep) {
    this.setState({ taskStep });
  }

  onSetUploadedChatbotAssets({ uploadedAssets }) {
    this.setState({
      uploadedChatbotAssets: [...this.state.uploadedChatbotAssets, ...uploadedAssets]
    });
  }

  onGetCloudinaryImagesByCompanyId() {
    this.setState({
      loadingCloudinaryImages: true
    });
  }

  onGetCloudinaryImagesByCompanyIdCompleted({ payload: { images = [], logos = [], videos = [] } }) {
    logger.log("AdAssistantStore", "onGetCloudinaryImagesByCompanyIdCompleted", {
      images,
      logos,
      videos
    });

    if (images.length === 0 && logos.length === 0 && videos.length === 0) {
      this.setState({ loadingCloudinaryImages: false });
      return;
    }

    const lastPreviewAssets = images.concat(videos);
    const { newUrls } = generateNewUrl({
      images: lastPreviewAssets.map((asset) => asset.image_url),
      uploadedImages: this.state.uploadedImages
    });

    const { newUrls: newLogos } = generateNewUrl({
      images: logos.map((logo) => logo.image_url),
      uploadedImages: this.state.uploadedLogos
    });

    this.setState({
      uploadedImages: [...this.state.uploadedImages, ...newUrls],
      uploadedLogos: [...this.state.uploadedLogos, ...newLogos],
      loadingCloudinaryImages: false
    });
  }

  onGetCloudinaryImagesByCompanyIdFailed(e) {
    this.setState({ loadingCloudinaryImages: false });
    this.onError(e);
  }

  onGetOldUploadedImagesCompleted(oldImages) {
    const oldImageUrls = oldImages.map((img) => img.image_url);
    this.setState({
      oldUploadedImages: oldImageUrls
    });
  }

  onSetPostData(newPostData) {
    this.setState({
      postData: {
        ...this.state.postData,
        ...newPostData
      }
    });
  }

  onSetText(newText) {
    this.setState({ text: newText });
  }

  onSetManualCampaignData(newCampaignData) {
    this.setState({ campaignData: { ...this.state.campaignData, ...newCampaignData } });
  }

  onCreateAssistantCampaignCompleted(data) {
    const { payload } = data;
    this.setState({ campaignData: payload });
  }

  onCreateAssistantCampaignFailed(e) {
    this.setState({ campaignData: {} });
    this.onError(e);
  }

  onGetAdCombinations() {
    this.setState({ loadingCombinations: true });
  }

  async onGetAdCombinationsCompleted(data) {
    const { payload } = data;

    const dimensions = getAdAssistantDimensions();

    const croppedCombinations = await processCroppedCombinations(payload, dimensions);

    const usedCombinations = croppedCombinations.filter(
      (combination) => combination.status === "used"
    );
    const dismissedCombinations = croppedCombinations.filter(
      (combination) => combination.status === "dismissed"
    );

    const lastUsedAd = [...croppedCombinations]
      .reverse()
      .find((combination) => combination.status === "used");
    const lastTextGenerated = lastUsedAd?.text?.long?.trim() || "";
    const defaultText = this.state.campaignData?.defaultText?.long?.trim() || "";

    const { shutterstockImages, shutterstockVideos, totalShutterstockAssetsPrice } =
      countShutterstockAssets(usedCombinations);

    this.setState({
      loadingCombinations: false,
      text: { long: lastTextGenerated || defaultText },
      allAdCombinations: croppedCombinations,
      usedAds: usedCombinations,
      dismissedAds: dismissedCombinations,
      shutterstockImages,
      shutterstockVideos,
      totalShutterstockAssetsPrice
    });
  }

  onGetAdCombinationsFailed(e) {
    this.setState({ loadingCombinations: false });
    this.onError(e);
  }

  onGetAssistantCampaign() {
    this.setState({ loadingAssistantCampaign: true });
  }

  onGetAssistantCampaignCompleted(data) {
    const { payload } = data;
    const { jobTitle } = payload;
    const isBooked = payload.status !== "NEW" && payload.status !== "DRAFT";
    const isWaitingForApproval = payload.status === "WAITING_FOR_APPROVAL";
    const isActive = payload.status === "ACTIVE";

    const isLocked = payload.isLocked || false;
    const lockedBy = payload.lockedBy || null;
    const lockedUserId = payload.lockedUserId || null;

    this.setState({
      campaignData: payload,
      jobTitle,
      loadingAssistantCampaign: false,
      isBooked,
      isWaitingForApproval,
      isActive,
      isLocked,
      lockedBy,
      lockedUserId
    });
  }

  onGetAssistantCampaignFailed(e) {
    this.setState({ loadingAssistantCampaign: false });
    this.onError(e);
  }

  onGetJobTitlesByCategoryIdCompleted(data) {
    const { jobTitles } = data;
    this.setState({ jobTitles });
  }

  onGetJobTitlesByCategoryIdFailed(e) {
    this.onError(e);
  }

  onSetCurrentAd(currentAd) {
    this.setState({ currentAd, text: { long: currentAd?.text?.long } });
  }

  onSetCurrentAdVersion(currentAdVersion) {
    const { id } = this.state.currentAd;
    this.setState({ currentAd: { ...currentAdVersion, id, versionId: currentAdVersion.id } });
  }

  onResetAssistantData() {
    this.setState({
      currentAd: {},
      allAdCombinations: [],
      usedAds: [],
      dismissedAds: [],
      text: {},
      campaignData: {},
      loadingAssistantCampaign: false,
      loadingCombinations: false,
      shutterstockImages: 0,
      shutterstockVideos: 0,
      totalShutterstockAssetsPrice: 0
    });
  }

  onSetAllAdCombinations(allAdCombinations) {
    const usedAdsCombinations = allAdCombinations.filter((ad) => ad.status === "used");
    const dismissedAdsCombinations = allAdCombinations.filter((ad) => ad.status === "dismissed");

    const { shutterstockImages, shutterstockVideos, totalShutterstockAssetsPrice } =
      countShutterstockAssets(usedAdsCombinations);

    this.setState({
      allAdCombinations,
      usedAds: usedAdsCombinations,
      dismissedAds: dismissedAdsCombinations,
      shutterstockImages,
      shutterstockVideos,
      totalShutterstockAssetsPrice
    });
  }

  onSetEditModalOpen(isOpen) {
    this.setState({ isEditModalOpen: isOpen });
  }

  onSetCompanyId(companyId) {
    this.setState({ companyId });
  }

  // Get all versions for versions history
  onGetAdVersions() {
    // Only clear versions if we're starting from the beginning (offset 0)
    if (this.state.versionsOffset === 0) {
      this.setState({ isLoadingVersions: true, versionsOffset: 0, adVersions: [] });
    } else {
      // Just set loading state without clearing existing versions
      this.setState({ isLoadingMoreVersions: true });
    }
  }

  onGetAdVersionsCompleted(data) {
    const { payload } = data;
    const hasMore = payload.length === this.state.versionsLimit;

    // If this is the initial load (offset 0), replace the versions
    // Otherwise, merge with existing versions using a Set to prevent duplicates
    const newVersions =
      this.state.versionsOffset === 0
        ? payload
        : Array.from(new Set([...this.state.adVersions, ...payload].map((v) => JSON.stringify(v))))
            .map((v) => JSON.parse(v))
            .sort((a, b) => b.version - a.version);

    this.setState({
      isLoadingVersions: false,
      isLoadingMoreVersions: false,
      adVersions: newVersions,
      hasMoreVersions: hasMore,
      versionsOffset: this.state.versionsOffset + this.state.versionsLimit
    });
  }

  onGetAdVersionsFailed(e) {
    this.setState({ isLoadingVersions: false });
    this.onError(e);
  }

  // Load more versions drag
  onLoadMoreVersions() {
    ActionsAdAssistant.getAdVersions(this.state.currentAd.id, {
      limit: this.state.versionsLimit,
      offset: this.state.versionsOffset
    });
  }

  // Set active version for Done button
  onSetActiveAdVersion() {
    this.setState({ isLoadingActiveAdVersion: true });
  }

  onEmptyAdVersions() {
    this.setState({ adVersions: [] });
  }

  onSetActiveAdVersionCompleted() {
    this.setState({ isLoadingActiveAdVersion: false });
    Actions.addNotification("Version set as active", "success");
  }

  onSetActiveAdVersionFailed(e) {
    this.setState({ isLoadingActiveAdVersion: false });
    this.onError(e);
  }
}

const generateNewUrl = ({ images = [], uploadedImages = [] }) => {
  const normalizeUrl = (url) => url.replace(/^https?:\/\//, "");
  const uploadedImageSet = new Set(uploadedImages.map(normalizeUrl));
  const newUrls = images.filter((url) => !uploadedImageSet.has(normalizeUrl(url)));

  return { newUrls };
};

const processCroppedCombinations = async (payload, dimensions) => {
  return Promise.all(
    payload.map(async (combination) => {
      // Enforce the order using a predefined structure
      const croppedMediaAssets = {
        vertical: null,
        portrait: null,
        landscape: null
      };

      for (const key of ["vertical", "portrait", "landscape"]) {
        if (combination.mediaAssets.hasOwnProperty(key)) {
          const mediaAsset = combination.mediaAssets[key];
          let updatedMediaAsset = { ...mediaAsset };

          if (mediaAsset.type === "image" && mediaAsset.inputProps?.backgroundImage) {
            const isShutterstockImage =
              mediaAsset.inputProps?.backgroundImage?.includes("shutterstock");

            const assetToCrop = isShutterstockImage
              ? getShutterstockPreviewUrl(mediaAsset.inputProps.backgroundImage)
              : mediaAsset.inputProps.backgroundImage;

            const assetHasParams = hasParamsAlready.test(assetToCrop);

            if (assetHasParams) {
              updatedMediaAsset.inputProps.backgroundImage = assetToCrop;
            } else {
              updatedMediaAsset.inputProps.backgroundImage = await processAutocroppedImage(
                assetToCrop,
                mediaAsset.inputProps.orientation,
                dimensions,
                mediaAsset.templateId
              );
            }
          } else if (mediaAsset.type === "video" && mediaAsset.inputProps?.video) {
            const assetToCrop = mediaAsset.inputProps.video;
            const videoDuration = await getVideoDuration(assetToCrop);

            // TODO: Not all crop params are working
            // updatedMediaAsset.inputProps.video = await processAutocroppedImage(
            //   assetToCrop,
            //   mediaAsset.inputProps.orientation,
            //   dimensions,
            //   mediaAsset.templateId
            // );

            updatedMediaAsset.inputProps.duration = videoDuration;
          }

          // Assign media asset to the correct key in order
          croppedMediaAssets[key] = updatedMediaAsset;
        }
      }

      return {
        ...combination,
        mediaAssets: croppedMediaAssets // Ensures order: vertical → portrait → landscape
      };
    })
  );
};
